"use client"

import { useState, useEffect, useCallback } from "react"
import { Category, CategoryFromDB, Project, Subcategory, SubcategoryInfo } from "../../../lib/types"
import { initialCategories, initialProjects } from "../../../lib/mock-data"
import { AddCategoryModal } from "./add-category-modal"
import { CategoryList } from "./category-list"
import { CategoryDetail } from "./category-detail"
import { ShareAllCategoriesModal } from "./share-all-categories-modal"
import { ArrowLeft, FolderKanban } from "lucide-react"
import { Share2Icon } from "@radix-ui/react-icons"
import { supbaseManager, uploadCategoryThumbnail, getUserId } from "../../../API"
import { Button } from "../../../components/ui/button"
import {
    OptionsControlModal,
    defaultConfig,
    addColorOptions,
    createColorOption,
    type OptionsConfig,
    type ColorOption,
} from "../options-control-modal"
import type { ColorTable } from "../../../lib/types"


declare global {
    interface Window {
        // Projects
        loadProjects: (projects: Project[]) => void
        updateProjects: (projects: Project[]) => void
        addProject: (project: Project) => void
        removeProject: (projectId: string) => void
        getProjects: () => Project[]
        // Categories
        loadCategories: (categoriesFromDB: CategoryFromDB[]) => void
        updateCategories: (categoriesFromDB: CategoryFromDB[]) => void
        addCategory: (categoryFromDB: CategoryFromDB) => void
        removeCategory: (categoryId: string) => void
        getCategories: () => Category[]
    }
}

// A coluna project_id (tabela iframe_options) é numérica — não aceita a
// string "all" diretamente. Gera um número estável e único por usuário,
// pra reaproveitar/atualizar sempre a mesma linha em vez de criar duplicatas
// a cada "Gerar Link".
function hashUserIdToNumber(str: string): number {
    let hash = 5381
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) + hash + str.charCodeAt(i)) >>> 0
    }
    return hash
}

export default function Home() {
    const [categories, setCategories] = useState<Category[]>(initialCategories)
    const [projects, setProjects] = useState<Project[]>(initialProjects)
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)
    const [users, setUsers] = useState<
        { id: string; name: string; avatar?: string }[]
    >([]) as any

    // --- Compartilhamento global (todas as categorias) ---
    const [shareAllOpen, setShareAllOpen] = useState(false)
    const [shareAllPickerOpen, setShareAllPickerOpen] = useState(false)
    const [shareAllConfig, setShareAllConfig] = useState<OptionsConfig>(defaultConfig)
    const [shareAllColorOptions, setShareAllColorOptions] = useState<ColorOption[]>([])

    useEffect(() => {
        const loadTables = async () => {
            try {
                const res = await supbaseManager("get", "color-tables", null, getUserId())
                if (!res || res.length == 0) return

                const parsed: ColorTable[] = res.map((row: any) => {
                    const content = JSON.parse(row.content)
                    content.id = row.id.toString()
                    return content
                })

                const options = parsed.map((t) => createColorOption(t.id, t.name))
                setShareAllColorOptions(addColorOptions([], options))
            } catch (err) {
                console.error(err)
            }
        }

        loadTables()
    }, [])

    const handleSaveShareAll = (config: OptionsConfig) => {
        // O link de verdade é gerado pelo botão "Gerar Link" dentro do
        // OptionsControlModal (função handleShare), não por essa prop —
        // aqui só guardamos a config escolhida pra reabrir o modal com o
        // mesmo estado da última vez.
        setShareAllConfig(config)
    }

    useEffect(() => {
        ; (window as any).setUsersCategory = (userArray: any[]) => {
            setUsers(userArray)
        }

        return () => {
            delete (window as any).setUsersCategory
        }
    }, [])

    const loadProjects = useCallback((newProjects: Project[]) => {
        setProjects(newProjects)
    }, [])

    const updateProjects = useCallback((newProjects: Project[]) => {
        setProjects(newProjects)
    }, [])

    const addProject = useCallback((project: Project) => {
        setProjects((prev) => [...prev, project])
    }, [])

    const removeProject = useCallback((projectId: string) => {
        setProjects((prev) => prev.filter((p) => p.id !== projectId))
        // Also remove from categories (e das subcategorias, pra não deixar
        // referência órfã a um projeto que não existe mais)
        setCategories((prev) =>
            prev.map((category) => ({
                ...category,
                projectIds: category.projectIds.filter((id) => id !== projectId),
                subcategories: (category.subcategories || []).map((sub) => ({
                    ...sub,
                    projectIds: sub.projectIds.filter((id) => id !== projectId),
                })),
            }))
        )
    }, [])

    // Parse category from DB format to app format
    const parseCategoryFromDB = useCallback((categoryFromDB: CategoryFromDB): Category => {
        const parsed = JSON.parse(categoryFromDB.json)
        return {
            id: parsed.id,
            name: parsed.name,
            projectIds: (parsed.projectIds || []).map((id: number | string) => id),
            subcategories: (parsed.subcategories || []) as Subcategory[],
            saved: true,
            userIds: (parsed.userIds || []).map((id: number | string) => id),
            shared: categoryFromDB.shared,
            thumbnail: parsed.thumbnail,
        } as any
    }, [])

    const loadCategories = useCallback((categoriesFromDB: CategoryFromDB[]) => {
        const parsed = categoriesFromDB.map(parseCategoryFromDB);

        (window as any).categoriesLoaded = parsed;

        var array = [{ id: 'todos', label: 'Todos os meus projetos', index: 0 }];

        for (var i = 0; i < parsed.length; i++) {
            array.push({
                id: parsed[i].name + Math.random(), label: parsed[i].name, index: i
            })
        }

        (window as any)["setCategoriesProjects"](array);

        setCategories(parsed)
    }, [parseCategoryFromDB])

    const updateCategories = useCallback((categoriesFromDB: CategoryFromDB[]) => {
        const parsed = categoriesFromDB.map(parseCategoryFromDB)
        setCategories(parsed)
    }, [parseCategoryFromDB])

    const addCategoryFromDB = useCallback((categoryFromDB: CategoryFromDB) => {
        const parsed = parseCategoryFromDB(categoryFromDB)
        setCategories((prev) => [...prev, parsed])
    }, [parseCategoryFromDB])

    const removeCategoryById = useCallback((categoryId: string) => {
        setCategories((prev) => prev.filter((c) => c.id !== categoryId))
    }, [])

    useEffect(() => {
        // Projects
        window.loadProjects = loadProjects
        window.updateProjects = updateProjects
        window.addProject = addProject
        window.removeProject = removeProject
        window.getProjects = () => projects
        // Categories
        window.loadCategories = loadCategories
        window.updateCategories = updateCategories
        window.addCategory = addCategoryFromDB
        window.removeCategory = removeCategoryById
        window.getCategories = () => categories

        return () => {
            delete (window as Partial<Window>).loadProjects
            delete (window as Partial<Window>).updateProjects
            delete (window as Partial<Window>).addProject
            delete (window as Partial<Window>).removeProject
            delete (window as Partial<Window>).getProjects
            delete (window as Partial<Window>).loadCategories
            delete (window as Partial<Window>).updateCategories
            delete (window as Partial<Window>).addCategory
            delete (window as Partial<Window>).removeCategory
            delete (window as Partial<Window>).getCategories
        }
    }, [
        loadProjects,
        updateProjects,
        addProject,
        removeProject,
        projects,
        loadCategories,
        updateCategories,
        addCategoryFromDB,
        removeCategoryById,
        categories,
    ])

    const handleAddCategory = async (name: string, thumbnail?: string) => {
        const newCategory: Category = {
            id: Date.now().toString(),
            name,
            projectIds: [],
            subcategories: [],
            saved: false,
            userIds: [],
            shared: false
        }

        if (thumbnail) {
            // thumbnail chega como data URL (base64) do picker; sobe pro
            // Supabase e guarda só a URL pública na categoria, em vez do
            // base64 (que deixaria o JSON da categoria pesado).
            const uploadedUrl = await uploadCategoryThumbnail(newCategory.id, thumbnail)
            if (uploadedUrl) {
                ; (newCategory as any).thumbnail = uploadedUrl
            }
        }

        setCategories((prev) => [...prev, newCategory])
    }

    const handleUpdateCategoryThumbnail = async (category: Category, dataUrl: string) => {
        const uploadedUrl = await uploadCategoryThumbnail(category.id, dataUrl)
        if (!uploadedUrl) return

        const updated: Category = { ...category, thumbnail: uploadedUrl } as any

        setCategories((prev) => prev.map((c) => (c.id === category.id ? updated : c)))

        // Se já estava salva no banco, persiste a mudança também.
        if (category.saved) {
            await supbaseManager(
                "update",
                "categories_new",
                JSON.stringify(updated),
                category.id
            )
        }
    }

    const handleDeleteCategory = async (categoryId: string) => {

        if (window.confirm("Deletar categoria?")) {
            const deleted = await supbaseManager(
                "delete",
                "categories_new",
                categoryId,
            );

            setCategories((prev) => prev.filter((c) => c.id !== categoryId))
        }
    }

    const handleRemoveProject = (categoryId: string, projectId: string) => {
        setCategories((prev) =>
            prev.map((category) =>
                category.id === categoryId
                    ? {
                        ...category,
                        projectIds: category.projectIds.filter((id) => id !== projectId),
                        // Remove também de qualquer subcategoria, pra não
                        // sobrar referência a um projeto que já saiu da categoria
                        subcategories: (category.subcategories || []).map((sub) => ({
                            ...sub,
                            projectIds: sub.projectIds.filter((id) => id !== projectId),
                        })),
                    }
                    : category
            )
        )
        // Update selected category if it's the one being modified
        if (selectedCategory?.id === categoryId) {
            setSelectedCategory((prev) =>
                prev
                    ? {
                        ...prev,
                        projectIds: prev.projectIds.filter((id) => id !== projectId),
                        subcategories: (prev.subcategories || []).map((sub) => ({
                            ...sub,
                            projectIds: sub.projectIds.filter((id) => id !== projectId),
                        })),
                    }
                    : null
            )
        }
    }

    const handleViewProject = (categoryId: string, projectId: string) => {
        const project = projects.find((p) => p.id === projectId)
        $("#pro-" + projectId).trigger("click");
    }

    const handleAddProjects = (categoryId: string, newProjectIds: string[]) => {
        setCategories((prev) =>
            prev.map((category) =>
                category.id === categoryId
                    ? {
                        ...category,
                        projectIds: [...category.projectIds, ...newProjectIds],
                    }
                    : category
            )
        )
        // Update selected category if it's the one being modified
        if (selectedCategory?.id === categoryId) {
            setSelectedCategory((prev) =>
                prev
                    ? {
                        ...prev,
                        projectIds: [...prev.projectIds, ...newProjectIds],
                    }
                    : null
            )
        }
    }

    // --- Subcategorias ---
    // Mesma filosofia do restante do arquivo: só mexe no estado local.
    // A persistência no Supabase continua acontecendo pelo botão
    // "Atualizar Categoria" (que salva a categoria inteira, subcategorias
    // incluídas, já que tudo vive dentro do mesmo campo `json`).

    const handleAddSubcategory = useCallback((categoryId: string, name: string, thumbnail?: string) => {
        const newSubcategory: Subcategory = {
            id: Date.now().toString(),
            name,
            projectIds: [],
            thumbnail,
        }

        const applyAdd = (category: Category): Category => ({
            ...category,
            subcategories: [...(category.subcategories || []), newSubcategory],
        })

        setCategories((prev) =>
            prev.map((category) => (category.id === categoryId ? applyAdd(category) : category))
        )

        if (selectedCategory?.id === categoryId) {
            setSelectedCategory((prev) => (prev ? applyAdd(prev) : null))
        }
    }, [selectedCategory])

    const handleDeleteSubcategory = useCallback((categoryId: string, subcategoryId: string) => {
        const applyDelete = (category: Category): Category => ({
            ...category,
            subcategories: (category.subcategories || []).filter((s) => s.id !== subcategoryId),
        })

        setCategories((prev) =>
            prev.map((category) => (category.id === categoryId ? applyDelete(category) : category))
        )

        if (selectedCategory?.id === categoryId) {
            setSelectedCategory((prev) => (prev ? applyDelete(prev) : null))
        }
    }, [selectedCategory])

    const handleAddProjectsToSubcategory = useCallback((categoryId: string, subcategoryId: string, newProjectIds: string[]) => {
        const applyAdd = (category: Category): Category => ({
            ...category,
            // Garante que todo projeto adicionado numa subcategoria também
            // esteja na lista "flat" da categoria (não precisa mais
            // adicionar na categoria principal antes de organizar numa
            // subcategoria).
            projectIds: Array.from(new Set([...category.projectIds, ...newProjectIds])),
            subcategories: (category.subcategories || []).map((sub) =>
                sub.id === subcategoryId
                    ? { ...sub, projectIds: Array.from(new Set([...sub.projectIds, ...newProjectIds])) }
                    : sub
            ),
        })

        setCategories((prev) =>
            prev.map((category) => (category.id === categoryId ? applyAdd(category) : category))
        )

        if (selectedCategory?.id === categoryId) {
            setSelectedCategory((prev) => (prev ? applyAdd(prev) : null))
        }
    }, [selectedCategory])

    const handleRemoveProjectFromSubcategory = useCallback((categoryId: string, subcategoryId: string, projectId: string) => {
        const applyRemove = (category: Category): Category => ({
            ...category,
            subcategories: (category.subcategories || []).map((sub) =>
                sub.id === subcategoryId
                    ? { ...sub, projectIds: sub.projectIds.filter((id) => id !== projectId) }
                    : sub
            ),
        })

        setCategories((prev) =>
            prev.map((category) => (category.id === categoryId ? applyRemove(category) : category))
        )

        if (selectedCategory?.id === categoryId) {
            setSelectedCategory((prev) => (prev ? applyRemove(prev) : null))
        }
    }, [selectedCategory])

    const handleUpdateSubcategoryInfo = useCallback((categoryId: string, subcategoryId: string, info: SubcategoryInfo) => {
        const applyUpdate = (category: Category): Category => ({
            ...category,
            subcategories: (category.subcategories || []).map((sub) =>
                sub.id === subcategoryId ? { ...sub, info } : sub
            ),
        })

        setCategories((prev) =>
            prev.map((category) => (category.id === categoryId ? applyUpdate(category) : category))
        )

        if (selectedCategory?.id === categoryId) {
            setSelectedCategory((prev) => (prev ? applyUpdate(prev) : null))
        }
    }, [selectedCategory])

    const handleUpdateCategory = (updated: Category) => {
        setCategories((prev) =>
            prev.map((cat) =>
                cat.id === updated.id ? updated : cat
            )
        )
    }

    // Aplica a seleção de categorias feita no ShareAllCategoriesModal: marca
    // cada categoria própria com includeInShareAll (true/false) e persiste
    // no Supabase só as que já foram salvas antes — depois segue pro fluxo
    // de geração do link "s=all" que já existia (OptionsControlModal).
    const handleConfirmShareAllSelection = async (includedIds: string[]) => {
        const updated = categories.map((c) =>
            !c.shared ? ({ ...c, includeInShareAll: includedIds.includes(c.id) } as Category) : c
        )

        setCategories(updated)

        await Promise.all(
            updated
                .filter((c) => !c.shared && c.saved)
                .map((c) => supbaseManager("update", "categories_new", JSON.stringify(c), c.id))
        )

        const userId = getUserId() || ""
        ; (window as any).categoryShared = userId + "&s=all"
        ; (window as any).projectSharedID = hashUserIdToNumber(userId + "-share-all")
        setShareAllOpen(true)
    }

    return (
        <main className="w-full h-full bg-background absolute z-50 left-0 top-0 hidden" id="category-new">
            <div className="container mx-auto py-8 px-4 overflow-y-auto">
                {selectedCategory ? (
                    <CategoryDetail
                        category={selectedCategory}
                        allProjects={projects}
                        onBack={() => setSelectedCategory(null)}
                        onRemoveProject={handleRemoveProject}
                        onAddProjects={handleAddProjects}
                        onViewProject={handleViewProject}
                        onAddSubcategory={handleAddSubcategory}
                        onDeleteSubcategory={handleDeleteSubcategory}
                        onAddProjectsToSubcategory={handleAddProjectsToSubcategory}
                        onRemoveProjectFromSubcategory={handleRemoveProjectFromSubcategory}
                        onUpdateSubcategoryInfo={handleUpdateSubcategoryInfo}
                    />
                ) : (
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="gap-2 bg-transparent"
                                    id="btn-close-categories-new"
                                >
                                    <ArrowLeft className="h-4 w-4" />
                                    Voltar
                                </Button>
                                <div className="rounded-lg bg-primary p-2">
                                    <FolderKanban className="h-6 w-6 text-primary-foreground" />
                                </div>
                                <div>
                                    <h1 className="text-2xl font-bold tracking-tight">
                                        Gerenciador de Categorias
                                    </h1>
                                    <p className="text-muted-foreground">
                                        Organize seus projetos em categorias
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    className="gap-2"
                                    onClick={() => setShareAllPickerOpen(true)}
                                >
                                    <Share2Icon className="h-4 w-4" />
                                    Compartilhar Todas
                                </Button>
                                <ShareAllCategoriesModal
                                    open={shareAllPickerOpen}
                                    onOpenChange={setShareAllPickerOpen}
                                    categories={categories.filter((c) => !c.shared)}
                                    onConfirm={handleConfirmShareAllSelection}
                                />
                                <OptionsControlModal
                                    open={shareAllOpen}
                                    onOpenChange={setShareAllOpen}
                                    initialConfig={shareAllConfig}
                                    onSave={handleSaveShareAll}
                                    colorOptions={shareAllColorOptions}
                                    showBranding={true}
                                />
                                <AddCategoryModal onAddCategory={handleAddCategory} />
                            </div>
                        </div>

                        <CategoryList
                            users={users}
                            categories={categories}
                            onSelectCategory={setSelectedCategory}
                            onDeleteCategory={handleDeleteCategory}
                            onUpdateCategory={handleUpdateCategory}
                            onUpdateCategoryThumbnail={handleUpdateCategoryThumbnail}
                        />
                    </div>
                )}
            </div>
        </main>
    )
}